import React, { useEffect, useState } from 'react';
import { distributorService } from '../services/api';
import { updateMetaTags } from '../utils/seo';
import { Globe, Mail, Phone, MapPin, X, ExternalLink, Briefcase, ShieldCheck } from 'lucide-react';

function Distributors() {
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDistributor, setSelectedDistributor] = useState(null);

  useEffect(() => {
    loadDistributors();
    updateMetaTags({
      title: 'Our Distribution Network',
      description: 'Connecting premium food and beverages with the consumer generation through our trusted distribution partners.'
    });
  }, []);

  const loadDistributors = async () => {
    try {
      const data = await distributorService.index();
      setDistributors(data.data || data || []);
    } catch (error) {
      console.error('Error loading distributors:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 py-20 px-4">
        <div className="max-w-7xl mx-auto text-center space-y-4">
          <p className="text-red-400 font-bold tracking-[0.3em] uppercase text-sm">Supply Chain Excellence</p>
          <h1 className="text-4xl md:text-6xl font-black text-white">Our Distributors</h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">Connecting premium food and beverages with the consumer generation through our trusted global distribution network.</p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 -mt-10 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {distributors.map((d) => (
            <div 
              key={d.id} 
              onClick={() => setSelectedDistributor(d)}
              className="bg-white p-8 rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 hover:border-red-500/30 transition-all duration-300 group cursor-pointer hover:-translate-y-1"
            >
              <div className="flex justify-between items-start mb-6">
                {d.logo ? (
                  <img src={d.logo} alt={d.company_name} className="h-14 w-14 rounded-2xl object-cover border border-slate-100" />
                ) : (
                  <div className="bg-red-50 p-3 rounded-2xl text-red-600 group-hover:bg-red-600 group-hover:text-white transition-all duration-300">
                    <Globe size={24} />
                  </div>
                )}
                <span className={`text-[10px] uppercase font-black tracking-widest px-3 py-1 rounded-full ${d.status === 'active' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                  {d.status || 'Active'}
                </span>
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 mb-2 group-hover:text-red-700 transition-colors">{d.company_name}</h3>
              <p className="text-red-600 font-bold text-sm mb-6 flex items-center gap-2">
                <Globe size={14} />
                {d.region} &middot; {d.territory}
              </p>
              
              <div className="space-y-3 pt-6 border-t border-slate-100 mt-auto">
                <div className="flex items-center gap-3 text-slate-600 text-sm font-medium">
                  <Briefcase size={14} className="text-slate-400" />
                  <span>{d.contact_person || 'Authorized Partner'}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600 text-sm font-mono">
                  <Mail size={14} className="text-slate-400" />
                  <span>{d.email}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {distributors.length === 0 && (
          <div className="text-center py-32 bg-white rounded-[2rem] border-2 border-dashed border-slate-200">
            <div className="bg-slate-50 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 text-slate-300">
              <Globe size={40} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No partners found</h3>
            <p className="text-slate-400 max-w-sm mx-auto">We are currently expanding our distribution network in this region.</p>
          </div>
        )}
      </main>

      {selectedDistributor && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="relative h-48 bg-slate-900">
              <button 
                onClick={() => setSelectedDistributor(null)} 
                className="absolute top-6 right-6 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all z-10"
              >
                <X size={20} />
              </button>
              
              <div className="absolute -bottom-12 left-10">
                <div className="h-32 w-32 rounded-[2rem] bg-white p-1 shadow-2xl border-4 border-white">
                  {selectedDistributor.logo ? (
                    <img src={selectedDistributor.logo} alt={selectedDistributor.company_name} className="h-full w-full object-cover rounded-[1.75rem]" />
                  ) : (
                    <div className="h-full w-full bg-slate-100 rounded-[1.75rem] flex items-center justify-center text-red-600 text-4xl font-black">
                      {selectedDistributor.company_name?.charAt(0)}
                    </div>
                  )}
                </div>
              </div>

              <div className="absolute top-10 left-10 px-4 py-1.5 rounded-full bg-red-500 text-white text-[10px] font-black uppercase tracking-[0.2em] shadow-lg shadow-red-500/30">
                {selectedDistributor.type?.replace('_', ' ') || 'Distributor'} Partner
              </div>
            </div>

            <div className="px-10 pt-16 pb-12 space-y-8">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-3xl font-black text-slate-900">{selectedDistributor.company_name}</h3>
                  <div className="flex items-center gap-2 mt-2 text-red-600 font-bold">
                    <Globe size={16} />
                    <span>{selectedDistributor.region} &bull; {selectedDistributor.territory}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1">Status</div>
                  <div className="flex items-center gap-2 text-red-600 font-bold uppercase text-xs">
                    <ShieldCheck size={14} />
                    {selectedDistributor.status}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 block mb-2">Primary Location</label>
                    <p className="text-slate-600 text-sm leading-relaxed font-medium">
                      {selectedDistributor.address}, {selectedDistributor.city}<br />
                      {selectedDistributor.state}, {selectedDistributor.postal_code}
                    </p>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 block mb-2">Network ID</label>
                    <div className="px-3 py-1 bg-slate-50 border border-slate-100 rounded-lg text-xs font-mono text-slate-500 inline-block">
                      #ORT-DIST-{selectedDistributor.id.toString().padStart(5, '0')}
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 block mb-2">Distribution Officer</label>
                    <div className="space-y-3">
                      <div className="text-slate-800 font-bold text-sm mb-1">{selectedDistributor.contact_person}</div>
                      <a href={`mailto:${selectedDistributor.email}`} className="flex items-center gap-3 text-slate-600 text-sm font-medium hover:text-red-600 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400">
                          <Mail size={14} />
                        </div>
                        {selectedDistributor.email}
                      </a>
                      <a href={`tel:${selectedDistributor.phone}`} className="flex items-center gap-3 text-slate-600 text-sm font-medium hover:text-red-600 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400">
                          <Phone size={14} />
                        </div>
                        {selectedDistributor.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-slate-100 flex justify-center">
                <p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Official Ortus FMCG Global Distribution Partner</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Distributors;
